import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Dialog,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { parseDate, getLocalTimeZone } from '@internationalized/date';
import DatePickerField from "../../../common/DatePickerDashboard";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllTaskAction,
  saveTaskAction,
  searchUserOnTaskAction,
  updateTaskAction,
  deleteTaskActionById,
  getCustomerFromPolicyAction,
  getTypeOfTaskOpenListAction
} from "../../../redux/actions/taskActions";
import {
  getCustomerListAction,
  getCustomerDataAction,
} from "../../../redux/actions/customerSearchAction";

const TaskComponent = ({
  open = false,
  handleClose,
  onSubmit,
  setIsEditTask,
  onDelete,
  isEditing,
  editId,
  setTaskComponentOpen,
  fromCalendarView,
  handleSnackbar,
  isLoading,
  setIsLoading,
  getTaskData,
}) => {
  const defaultValues = {
    policyNumber: null,
    assignedToName: null,
    dueDate: null,
    status: "",
    priority: "",
    taskType: "",
    taskDescription: "",
    clientCustomerId: "",
    customer: null,
  };

  const dispatch = useDispatch();
  const initializationComplete = useRef(false);
  const userSearchTimeoutRef = useRef(null);
  
  const [formValues, setFormValues] = useState({
    policyId: null,
    customer: null,
    policyNumber: null,
    createdDate: null,
    closedBy: "",
    assignedToName: "",
    assignedTo: "",
    dueDate: null,
    status: "",
    priority: "",
    taskType: "",
    taskDescription: "",
    clientCustomerId: "",
  });
  
  const [taskFormType, setTaskFormType] = useState("");
  const [isCustomerSelected, setIsCustomerSelected] = useState(false);
  const [errors, setErrors] = useState({});
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [originalTaskFormType, setOriginalTaskFormType] = useState(null);
  const [formData, setFormData] = useState(() => {
    const getToday = () => {
      const today = new Date();
      return parseDate(
        `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      );
    };

    const getFirstOfMonth = () => {
      const today = new Date();
      return parseDate(
        `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`
      );
    };

    const formatForForm = (date) => {
      const d = date.toDate(getLocalTimeZone());
      return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
    };

    return {
      fromDate: formatForForm(getFirstOfMonth()),
      toDate: formatForForm(getToday()),
    };
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Selectors
  const searchUsers = useSelector(
    (state) => state.task.searchUser?.content || []
  );
  const ClientCustomerIdFromPolicy = useSelector(
    (state) => state?.task?.policyDetails || null
  );
  const finalClientCustomerId = ClientCustomerIdFromPolicy?.clientCustomerId || null;
  const taskTypes = useSelector(
    (state) => state?.task?.type_task_list || []
  );
  const customers = useSelector(
    (state) => state?.globalSearchReducer?.customer_search_list.content || []
  );
  const policies = useSelector(
    (state) => state?.globalSearchReducer?.customer_data?.content || []
  );

  const fallbackCustomer = useMemo(() => {
    if (!isEditing || !getTaskData) return null;

    if (getTaskData.clientCustomerId) {
      const [firstName = "", lastName = ""] = getTaskData.customerName?.split(" ") || ["", ""];
      return {
        id: getTaskData.clientCustomerId,
        firstName,
        lastName,
      };
    } else if (getTaskData.policyId) {
      const selectedPolicy = policies.find((p) => p.id === getTaskData.policyId);
      if (selectedPolicy) {
        const [firstName = "", lastName = ""] = selectedPolicy.customerName?.split(" ") || ["", ""];
        return {
          id: selectedPolicy.clientCustomerId || null,
          firstName,
          lastName,
          customerNumber: selectedPolicy.customerNumber || "",
        };
      }
    } else if (!getTaskData.clientCustomerId && getTaskData.customerName) {
      const [firstName = "", lastName = ""] = getTaskData.customerName.split(" ");
      return {
        id: null,
        firstName,
        lastName,
        customerNumber: getTaskData.customerNumber || "",
      };
    }

    return null;
  }, [isEditing, getTaskData, policies]);

  const extendedCustomerList = useMemo(() => {
    if (
      fallbackCustomer &&
      !customers.some(
        (c) =>
          c.id === fallbackCustomer.id ||
          (c.firstName === fallbackCustomer.firstName &&
           c.lastName === fallbackCustomer.lastName)
      )
    ) {
      return [fallbackCustomer, ...customers];
    }
    return customers;
  }, [fallbackCustomer, customers]);

  const handleDateChange = useCallback((fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));

    if (fieldName === "dueDate") {
      setFormValues((prev) => ({
        ...prev,
        dueDate: dayjs(value, "MM-DD-YYYY"),
      }));
      setErrors((prev) => ({ ...prev, dueDate: undefined }));
    }
  }, []);

  const handleBlur = useCallback((fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  }, []);

  const fetchTasks = useCallback(() => {
    setTimeout(() => {
      dispatch(getAllTaskAction(0, 200));
    }, 1000);
  }, [dispatch]);

  const handleUserInputChange = useCallback((event, newInputValue) => {
    if (userSearchTimeoutRef.current) {
      clearTimeout(userSearchTimeoutRef.current);
    }
    userSearchTimeoutRef.current = setTimeout(() => {
      if (newInputValue?.length >= 3) {
        dispatch(searchUserOnTaskAction(newInputValue));
      }
    }, 300);
  }, [dispatch]);

  const handleCustomerSearch = useCallback((event, newInputValue) => {
    setCustomerSearchTerm(newInputValue);
  }, []);

  const handleTaskFormTypeChange = useCallback((event) => {
    const newTaskFormType = event.target.value;
    setTaskFormType(newTaskFormType);
    
    if (newTaskFormType === "Customer") {
      setFormValues(prev => ({
        ...prev,
        policyNumber: ""
      }));
      setErrors(prev => ({
        ...prev,
        policyNumber: undefined
      }));
    }
  }, []);

  useEffect(() => {
    if (!open) {
      initializationComplete.current = false;
      return;
    }

    if (initializationComplete.current) return;

    if (isEditing && getTaskData) {
      const isPolicy = !!getTaskData.policyId;
      const isCustomer = !!getTaskData.clientCustomerId && !getTaskData.policyId;

      const detectedFormType = isPolicy
        ? "Policies"
        : isCustomer
        ? "Customer"
        : "Policies";

      setTaskFormType(detectedFormType);
      setOriginalTaskFormType(detectedFormType);

      let fallbackCustomer = null;

      if (getTaskData.clientCustomerId) {
        const [firstName = "", lastName = ""] = getTaskData.customerName
          ? getTaskData.customerName.split(" ")
          : ["", ""];
        fallbackCustomer = {
          id: getTaskData.clientCustomerId,
          firstName,
          lastName,
          customerNumber: getTaskData.customerNumber || "",
        };
      }

      const formatToMMDDYYYY = (dateStr) => {
        if (!dateStr) return null;
        const dateObj = dayjs(dateStr);
        return dateObj.isValid()
          ? dateObj.format("MM-DD-YYYY")
          : null;
      };

      setFormValues({
        ...getTaskData,
        customer: fallbackCustomer,
        policyNumber: getTaskData.policyId || "",
        dueDate: getTaskData.dueDate ? dayjs(getTaskData.dueDate) : null,
      });

      setFormData((prev) => ({
        ...prev,
        dueDate: formatToMMDDYYYY(getTaskData.dueDate),
      }));

      setIsCustomerSelected(true);

      if (
        getTaskData.clientCustomerId &&
        getTaskData.clientCustomerId !== "null"
      ) {
        dispatch(getCustomerDataAction(getTaskData.clientCustomerId));
      }
    } else if (!isEditing) {
      setFormValues(defaultValues);
      setTaskFormType("Policies");
      setIsCustomerSelected(false);
      setErrors({});
      setOriginalTaskFormType(null);
    }

    initializationComplete.current = true;
  }, [isEditing, getTaskData, dispatch, open, defaultValues]);

  useEffect(() => {
    if (formValues.clientCustomerId && taskFormType === "Policies" && isCustomerSelected) {
      dispatch(getCustomerDataAction(formValues.clientCustomerId));
    }
  }, [formValues.clientCustomerId, taskFormType, isCustomerSelected, dispatch]);

  useEffect(() => {
    if (customerSearchTerm.length >= 2) {
      dispatch(getCustomerListAction({ name: customerSearchTerm }));
    }
  }, [customerSearchTerm, dispatch]);

  useEffect(() => {
    if (
      isEditing &&
      taskFormType === "Policies" &&
      ClientCustomerIdFromPolicy &&
      ClientCustomerIdFromPolicy.firstName
    ) {
      const { firstName, lastName, clientCustomerId, customerNumber } = ClientCustomerIdFromPolicy;

      const constructedCustomer = {
        id: clientCustomerId || null,
        firstName,
        lastName,
        customerNumber: customerNumber || "",
      };

      setFormValues((prev) => ({
        ...prev,
        customer: constructedCustomer,
        clientCustomerId: clientCustomerId || "",
      }));

      setIsCustomerSelected(true);
    }
  }, [isEditing, taskFormType, ClientCustomerIdFromPolicy]);

  useEffect(() => {
    if (taskFormType === "Customer") {
      setErrors((prev) => ({ ...prev, policyNumber: undefined }));
    }
  }, [taskFormType]);

  const validateForm = useCallback(() => {
    let tempErrors = {};
    let tempFieldErrors = {};

    setTouchedFields((prev) => ({ ...prev, dueDate: true }));

    if (!formValues.customer) tempErrors.customer = "Search Customer is required";

    if (!formValues.dueDate) {
      tempErrors.dueDate = "Select Due Date is required";
      tempFieldErrors.dueDate = "Select Due Date is required";
    }

    if (!formValues.assignedToName) tempErrors.assignedToName = "Assigned To is required";
    if (!formValues.status) tempErrors.status = "Select Status";
    if (!formValues.priority) tempErrors.priority = "Select Task Priority";
    if (!formValues.taskType) tempErrors.taskType = "Select Type of Task";
    if (!formValues.taskDescription) tempErrors.taskDescription = "Task Description is required";

    if (taskFormType === "Policies" && !formValues.policyNumber) {
      tempErrors.policyNumber = "Select a policy";
    }

    setErrors(tempErrors);
    setFieldErrors(tempFieldErrors);
    return Object.keys(tempErrors).length === 0;
  }, [formValues, taskFormType]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
    setErrors(prevErrors => ({ ...prevErrors, [name]: undefined }));
  }, []);

const handleSubmit = useCallback(() => {
  if (!validateForm()) return;

  const selectedPolicy = policies.find(
    (p) => p.id === formValues.policyNumber
  );

  let formattedFormValues = {
    ...(taskFormType === "Customer"
      ? {
          clientCustomerId: formValues.clientCustomerId || finalClientCustomerId || null,
          customerName: formValues.customer
            ? `${formValues.customer.firstName || ""} ${formValues.customer.lastName || ""}`.trim()
            : null,
          customerNumber: formValues.customer?.customerNumber || null,
        }
      : taskFormType === "Policies"
      ? {
          policyId: formValues.policyNumber,
          customerName:
            selectedPolicy?.customerName ||
            (formValues.customer
              ? `${formValues.customer.firstName || ""} ${formValues.customer.lastName || ""}`.trim()
              : null),
          customerNumber:
            selectedPolicy?.customerNumber || formValues.customer?.customerNumber || null,
        }
      : {}),
    assignedTo: formValues.assignedTo,
    status: formValues.status,
    priority: formValues.priority,
    taskType: formValues.taskType,
    taskDescription: formValues.taskDescription,
    dueDate: formValues.dueDate
      ? dayjs(formValues.dueDate).format("YYYY-MM-DD")
      : null,
  };

  if (formattedFormValues.policyId && formattedFormValues.clientCustomerId) {
    if (taskFormType === "Customer") {
      delete formattedFormValues.policyId;
    } else if (taskFormType === "Policies") {
      delete formattedFormValues.clientCustomerId;
    }
  }

  const hasFormTypeChanged =
    isEditing && originalTaskFormType && originalTaskFormType !== taskFormType;

  if (isEditing && !hasFormTypeChanged) {
    const finalUpdatedValue = { ...formattedFormValues, id: editId };

    handleClose();
    setIsLoading(true);

    dispatch(
      updateTaskAction(
        finalUpdatedValue,
        () => {
          handleSnackbar("Task updated successfully!", "success");
          setIsLoading(false);
          
          if (onSubmit && typeof onSubmit === 'function') {
            setTimeout(() => {
              onSubmit(finalUpdatedValue);
            }, 100);
          }
          
          if (fetchTasks && typeof fetchTasks === 'function') {
            setTimeout(() => {
              fetchTasks();
            }, 1000);
          }
        },
        (error) => {
          setIsLoading(false);
          handleSnackbar(error || "Task update failed!", "error");
        }
      )
    );
  } else if (isEditing && hasFormTypeChanged) {
    setTaskComponentOpen(false);
    setIsLoading(true);

    dispatch(
      saveTaskAction(
        formattedFormValues,
        () => {
          setIsLoading(false);
          handleSnackbar("Task updated successfully!", "success");
          
          if (onSubmit && typeof onSubmit === 'function') {
            setTimeout(() => {
              onSubmit(formattedFormValues);
            }, 100);
          }
          
          if (fetchTasks && typeof fetchTasks === 'function') {
            setTimeout(() => {
              fetchTasks();
            }, 1000);
          }

          dispatch(
            deleteTaskActionById(
              isEditing.id,
              () => {
                if (fetchTasks && typeof fetchTasks === 'function') {
                  setTimeout(() => {
                    fetchTasks();
                  }, 1500);
                }
              },
              (error) => {
                handleSnackbar("New task created, but couldn't delete old task.", "warning");
                setIsLoading(false);
              }
            )
          );
        },
        (error) => {
          setIsLoading(false);
          handleSnackbar(error || "Task creation failed!", "error");
        }
      )
    );
     fetchTasks();
  } else {
    setTaskComponentOpen(false);
    setIsLoading(true);

    dispatch(
      saveTaskAction(
        formattedFormValues,
        () => {
            handleSnackbar("Task added successfully!", "success");
            fetchTasks();
            setIsLoading(false);
          },
        (error) => {
          setIsLoading(false);
          handleSnackbar(error || "Task creation failed!", "error");
        }
      )
    );
  }
}, [
  validateForm, 
  policies, 
  formValues, 
  taskFormType, 
  finalClientCustomerId, 
  isEditing, 
  originalTaskFormType, 
  editId, 
  handleClose, 
  setIsLoading, 
  dispatch, 
  handleSnackbar, 
  fetchTasks, 
  setTaskComponentOpen,
  onSubmit
]);
  const handleCustomerSelect = useCallback((event, newValue) => {
    if (newValue) {
      setFormValues(prev => ({ 
        ...prev, 
        customer: newValue, 
        clientCustomerId: newValue.id, 
        policyNumber: "" 
      }));
      setIsCustomerSelected(true);
      setErrors(prevErrors => ({ ...prevErrors, customer: undefined, policyNumber: undefined }));
      
      dispatch(getCustomerDataAction(newValue.id));
    } else {
      setFormValues(prev => ({ 
        ...prev, 
        customer: null, 
        clientCustomerId: "", 
        policyNumber: "" 
      }));
      setIsCustomerSelected(false);
      setErrors(prevErrors => ({ ...prevErrors, customer: "Search Customer is required" }));
    }
  }, [dispatch]);

  const handlePolicySelect = useCallback((event) => {
    const policyId = event.target.value;
    setFormValues(prev => ({ ...prev, policyNumber: policyId }));
    if (policyId) {
      setErrors(prev => ({ ...prev, policyNumber: undefined }));
      dispatch(getCustomerFromPolicyAction(policyId));
    }
  }, [dispatch]);

  const onCloseDialog = useCallback(() => {
    if (isLoading) return;
    setErrors({});
    setFormValues(defaultValues);
    setIsEditTask(false);
    setTaskFormType("");
    setIsCustomerSelected(false);
    setOriginalTaskFormType(null);
    initializationComplete.current = false;
    handleClose();
  }, [isLoading, defaultValues, setIsEditTask, handleClose]);

  const handleDeleteTask = useCallback(() => {
    if (typeof onDelete === "function" && !isLoading) {
      handleClose();
      onDelete();
    }
  }, [onDelete, isLoading, handleClose]);

  useEffect(() => {
    return () => {
      if (userSearchTimeoutRef.current) {
        clearTimeout(userSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    dispatch(getTypeOfTaskOpenListAction());
  }, [dispatch]);

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onCloseDialog(event, reason);
      }}
      disableEscapeKeyDown={isLoading}
      disableScrollLock={true}
      keepMounted={false}
      hideBackdrop={false}
      style={{ zIndex: 1300 }}
    >
      <DialogTitle>
        {isEditing ? "Edit Task" : "Add Task"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ width: "100%", mt: 2 }}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                name="taskFormType"
                value={taskFormType}
                onChange={handleTaskFormTypeChange}
              >
                <FormControlLabel
                  value="Customer"
                  control={<Radio />}
                  label="Customer"
                />
                <FormControlLabel
                  value="Policies"
                  control={<Radio />}
                  label="Policies"
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {(taskFormType === "Customer" || taskFormType === "Policies") && (
            <Box sx={{ width: "21%" }}>
              <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                Search Customer <span style={{ color: "red" }}>*</span>
              </Typography>
              <Autocomplete
                options={extendedCustomerList || []}
                value={formValues.customer}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id ||
                  (
                    option?.firstName?.toLowerCase() === value?.firstName?.toLowerCase() &&
                    option?.lastName?.toLowerCase() === value?.lastName?.toLowerCase()
                  )
                }
                getOptionLabel={(option) =>
                  `${option?.firstName || ""} ${option?.lastName || ""}`.trim()
                }
                onChange={handleCustomerSelect}
                onInputChange={handleCustomerSearch}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search customer..."
                    fullWidth
                    error={!!errors.customer}
                    helperText={errors.customer}
                  />
                )}
              />
            </Box>
          )}

          {taskFormType === "Policies" && (
            <Box sx={{ width: "21%" }}>
              <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                Select Policy <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                name="policyNumber"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={formValues.policyNumber || ""}
                onChange={handlePolicySelect}
                disabled={!isCustomerSelected}
                error={!!errors.policyNumber}
                helperText={errors.policyNumber}
              >
                <option value="" disabled>
                  Select Policy
                </option>
                {policies.map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    {policy.policyNumber}
                  </option>
                ))}
              </TextField>
            </Box>
          )}

          <Box sx={{ width: "21%" }}>
            <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
              Assigned To <span style={{ color: "red" }}>*</span>
            </Typography>
            <Autocomplete
              options={searchUsers}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                return option?.firstName
                  ? `${option.firstName} ${option.lastName || ""}`
                  : "";
              }}
              value={formValues.assignedToName || null}
              onChange={(event, newValue) => {
                const assignedId = newValue?.id || "";
                const assignedName = newValue
                  ? `${newValue.firstName || ""} ${newValue.lastName || ""}`
                  : "";

                setFormValues((prev) => ({
                  ...prev,
                  assignedToName: assignedName,
                  assignedTo: assignedId,
                }));

                setErrors((prevErrors) => ({
                  ...prevErrors,
                  assignedToName: newValue ? undefined : "Assigned To is required",
                }));
              }}
              disabled={!isCustomerSelected}
              onInputChange={handleUserInputChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search users..."
                  error={!!errors.assignedToName}
                  helperText={errors.assignedToName}
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  {typeof option === "string"
                    ? option
                    : `${option.firstName || ""} ${option.lastName || ""} ${option.email ? `(${option.email})` : ""}`}
                </li>
              )}
              freeSolo
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
            />
          </Box>
          <Box sx={{ width: "26%" }}>
             <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
              Select Due Date <span style={{ color: "red" }}>*</span>
            </Typography>
            <DatePickerField
                fieldName="dueDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
              />
          </Box>

          <Box sx={{ width: "21%" }}>
            <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
              Select Status <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              name="status"
              fullWidth
              select
              SelectProps={{ native: true }}
              value={formValues.status}
              onChange={handleChange}
              disabled={!isCustomerSelected}
              error={!!errors.status}
              helperText={errors.status}
            >
              <option value="" disabled>
                Select Status
              </option>
              <option value="Open">Open</option>
              {isEditing && <option value="Close">Close</option>}
            </TextField>
          </Box>
          <Box sx={{ width: "21%" }}>
            <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
              Task Priority <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              name="priority"
              fullWidth
              select
              SelectProps={{ native: true }}
              value={formValues.priority}
              disabled={!isCustomerSelected}
              onChange={handleChange}
              error={!!errors.priority}
              helperText={errors.priority}
            >
              <option value="" disabled>
                Select
              </option>
              <option value="URGENT">Urgent</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </TextField>
          </Box>
          <Box sx={{ width: "21%" }}>
            <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
              Type of Task <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              name="taskType"
              fullWidth
              select
              SelectProps={{ native: true }}
              value={formValues.taskType}
              onChange={(event) => {
                const { value } = event.target;
                setFormValues(prev => ({ ...prev, taskType: value }));
                if (value) {
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    taskType: undefined,
                  }));
                }
              }}
              disabled={!isCustomerSelected}
              error={!!errors.taskType}
              helperText={errors.taskType}
            >
              <option value="" disabled>Select</option>
              {taskTypes?.length > 0 ? (
                taskTypes.map((task, index) => (
                  <option key={index} value={task}>
                    {task}
                  </option>
                ))
              ) : (
                <option disabled>Loading...</option>
              )}
            </TextField>
          </Box>
          <Box sx={{ width: "98%" }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Task Description <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              name="taskDescription"
              fullWidth
              multiline
              rows={3}
              value={formValues.taskDescription}
              onChange={handleChange}
              disabled={!isCustomerSelected}
              error={!!errors.taskDescription}
              helperText={errors.taskDescription}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 2,
              width: "100%",
            }}
          >
            {isEditing && (
              <Button
                onClick={handleDeleteTask}
                color="error"
                variant="outlined"
              >
                Delete
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }}></Box>
            <Button
              onClick={onCloseDialog}
              variant="outlined"
            >
              Cancel
            </Button>
            {fromCalendarView ? (
              <Button
                onClick={() => {
                  if (isEditing && validateForm()) {
                    onSubmit({ ...formValues, taskFormType });
                  }
                }}
                variant="outlined"
              >
                Update
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={handleSubmit}
              >
                {isEditing ? "Update" : "Submit"}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TaskComponent;
